// AgentPromptStateMachine.js

import { runAIMatches, fetchIndustries, runMultiMatches, fetchSSE, fetchChatCompletion } from '../utils/api';
import { getTitleStrings } from './utils'
import AgentPrompts from './AgentPrompts';
import { API_CONFIG } from '../config/constants';
import { getDefaultPromptByTopic } from '../config/defaultPrompts';

export const DiscoveryTopics = [
    'CURRENT_JOB_TITLE_ROLE',
    'PAST_JOB_TITLE_ROLE',
    'LOCATION',
    'INDUSTRY',
    'COMPANY_NAME',
    'COMPANY_STAGE',
    'COMPANY_BUSINESS_MODEL',
    'COMPANY_SIZE',
    'SKILLS_MUSTHAVE',
    'SKILLS_NICETOHAVE',
    'OVERALL_EXPERIENCE',
    'SCHOOL_NAME',
    'DEGREE',
    'MAJOR',
    'OTHER_ATTRIBUTES',
    'OTHER'
];

export const DiscoveryStatus = {
    BEGIN: 'Begin',
    IN_PROGRESS: 'InProgress',
    VERIFICATION_IN_PROGRESS: 'VerificationInProgress',
    VERIFICATION_SUCCESS: 'VerificationSuccess',
    VERIFICATION_FAILED: 'VerificationFailed',
};

export class AgentPromptStateMachine {
    constructor() {
        this.discoveryState = {
            topics: DiscoveryTopics.map(topic => ({
                name: topic,
                status: DiscoveryStatus.BEGIN,
                value: []
            })),
            currentTopic: null,
            cachedTopic: null
        };
    }

    updateCurrentState(state) {
        this.discoveryState.currentTopic.status = state;
    }

    async processUserInput(userInput, currentTopic, queries) {
        // Cache the current topic for correlation
        this.discoveryState.cachedTopic = this.discoveryState.currentTopic;

        // Check the status of the current topic
        const topicState = this.discoveryState.topics.find(
            topic => topic.name === currentTopic
        );

        if (!topicState) {
            console.error('Invalid topic:', currentTopic);
            return;
        }

        if (topicState.status === DiscoveryStatus.VERIFICATION_SUCCESS) {
            this.moveToNextTopic();
            return;
        }

        let prompt_str = await this.sendDiscoveryPrompt(topicState, userInput, queries);
        return prompt_str;

        if (topicState.status === DiscoveryStatus.BEGIN || topicState.status === DiscoveryStatus.IN_PROGRESS) {
            let prompt_str = await this.sendDiscoveryPrompt(topicState, userInput, queries);
            return prompt_str;
        } else if (topicState.status === DiscoveryStatus.VERIFICATION_FAILED) {
            this.sendVerificationFailurePrompt(topicState);
        } else if (topicState.status === DiscoveryStatus.VERIFICATION_IN_PROGRESS) {
            this.sendVerificationPrompt(topicState);
        } else if (topicState.status === DiscoveryStatus.VERIFICATION_SUCCESS) {
            this.moveToNextTopic();
        }
    }

    async sendDiscoveryPrompt(topic, userInput, queries) {
        let currentTopic = null;
        if (topic.name == "OTHER") {
            let prompt = getDefaultPromptByTopic("OTHER");
            return prompt + ' current topic being: ' + this.discoveryState.currentTopic;
        } else {
            this.discoveryState.currentTopic = topic.name;
            currentTopic = this.discoveryState.topics.find(
                topic => topic.name === this.discoveryState.currentTopic
            );
            this.discoveryState.currentTopic = topic;
        }

        if (!currentTopic) return;

        console.log("sendDiscoveryPrompt: Current state is " + topic.name);
        if (topic.name == "CURRENT_JOB_TITLE_ROLE") {
            return await this.handleCurrentJobTitlePrompts(currentTopic, userInput, queries)
        } else if (topic.name == "PAST_JOB_TITLE_ROLE") {
            return await this.handlePastJobTitlePrompts(currentTopic, userInput, queries);
        }
    }

    async handleCurrentJobTitlePrompts(currentTopic, userInput, queries) {
        let prompt = '';
        if (currentTopic.status == DiscoveryStatus.VERIFICATION_FAILED) {
            // prompt = AgentPrompts.generatePrompt("CURRENT_JOB_TITLE_ROLE", null);
            prompt = getDefaultPromptByTopic("CURRENT_JOB_TITLE_ROLE");
            currentTopic.status = DiscoveryStatus.IN_PROGRESS;
            return prompt;
        }
        let matches = await runAIMatches(userInput);
        let roles = getTitleStrings(matches, queries);
        currentTopic.value = roles;
        prompt = AgentPrompts.generatePrompt("CURRENT_JOB_TITLE_ROLE", roles);
        if (roles.length > 0) {
            // verification prompt
            currentTopic.status = DiscoveryStatus.VERIFICATION_IN_PROGRESS;
        } else {
            // send role prompt
            currentTopic.status = DiscoveryStatus.IN_PROGRESS;
        }
        return prompt;
    }

    async handlePastJobTitlePrompts(currentTopic, userInput, queries) {
        let prompt = getDefaultPromptByTopic("PAST_JOB_TITLE_ROLE");;
        if (currentTopic.status == DiscoveryStatus.VERIFICATION_FAILED) {
            // prompt = AgentPrompts.generatePrompt("PAST_JOB_TITLE_ROLE", null);
            currentTopic.status = DiscoveryStatus.IN_PROGRESS;
            return prompt;
        }
        let roles = [];
        if (userInput != 'init') {
            // let monitorAgentInput = AgentPrompts.monitorAndExtractAgentPrompt(currentTopic.name);
            if (userInput) {
                // let response = await fetchChatCompletion(API_CONFIG.url, prompt + userInput, monitorAgentInput);
                let matches = await runAIMatches(userInput);
                roles = getTitleStrings(matches, queries);
                currentTopic.value = roles;
                // currentTopic.value = response.values;
                // if (response.values && (response.values.length > 0)) {
                if (roles && roles.length > 0) {
                    prompt = AgentPrompts.generatePrompt("PAST_JOB_TITLE_ROLE", roles);
                }
            }    
        }
        if (roles.length > 0) {
            // verification prompt
            currentTopic.status = DiscoveryStatus.VERIFICATION_IN_PROGRESS;
        } else {
            // send role prompt
            currentTopic.status = DiscoveryStatus.IN_PROGRESS;
        }
        return prompt;
    }

    sendVerificationPrompt(topic) {
        const prompt = `Verification needed for ${topic.name}. Confirm the following information: ${topic.value.join(', ')}.`;
        this.sendPromptToAI(prompt);
    }

    sendVerificationFailurePrompt(topic) {
        const prompt = `Verification failed for ${topic.name}. Ask the user to clarify or provide more details.`;
        this.sendPromptToAI(prompt);
    }

    handleAIResponse(response) {
        const currentTopic = this.discoveryState.topics.find(
            topic => topic.name === this.discoveryState.currentTopic
        );

        if (!currentTopic) return;

        if (response.type === 'verification') {
            if (response.verificationSuccess) {
                currentTopic.status = DiscoveryStatus.VERIFICATION_SUCCESS;
                this.moveToNextTopic();
            } else {
                currentTopic.status = DiscoveryStatus.VERIFICATION_FAILED;
                this.sendVerificationFailurePrompt(currentTopic);
            }
        } else if (response.type === 'discovery') {
            currentTopic.value.push(response.info);
            if (response.readyForVerification) {
                currentTopic.status = DiscoveryStatus.VERIFICATION_IN_PROGRESS;
                this.sendVerificationPrompt(currentTopic);
            } else {
                this.sendDiscoveryPrompt(currentTopic, response.followUp);
            }
        }
    }

    async moveToNextTopic() {
        const nextTopic = this.discoveryState.topics.find(
            topic => topic.status === DiscoveryStatus.BEGIN
        );

        if (nextTopic) {
            nextTopic.status = DiscoveryStatus.IN_PROGRESS;
            this.discoveryState.currentTopic = nextTopic.name;
            this.discoveryState.cachedTopic = nextTopic.name;
            console.log("next topic is " + nextTopic.name)
            return await this.sendDiscoveryPrompt(nextTopic, 'init');
        } else {
            return "";
            console.log('All topics are processed.');
        }
    }

    sendPromptToAI(prompt) {
        console.log('Sending to AI:', prompt);
        // Replace with actual AI integration
    }

    userChangedTopic(newTopicName) {
        const newTopic = this.discoveryState.topics.find(
            topic => topic.name === newTopicName
        );

        if (newTopic) {
            this.discoveryState.cachedTopic = this.discoveryState.currentTopic;
            this.discoveryState.currentTopic = newTopic.name;
            newTopic.status = DiscoveryStatus.IN_PROGRESS;
            this.sendPromptToAI(`Switching to ${newTopic.name}.`);
        } else {
            console.error('Invalid topic:', newTopicName);
        }
    }
}
